/** RecordingPanel — right-side 录音笔 (recorder pen) Drawer.
 *
 *  Two tabs:
 *  - 新建录音: real microphone capture via useMediaRecorder; after stop,
 *    a save form collects title / matter / meeting type / key-meeting flag,
 *    then creates a source=manual Meeting and uploads the audio blob.
 *  - 录音列表: list of the user's recorder-pen captures (source=manual &
 *    has_audio), with player + transcript editing + delete.
 *
 *  The toolbar button in GlobalTopBarActions toggles this Drawer.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, App } from "antd";
import {
  ReloadOutlined,
  CloseOutlined,
  AudioOutlined,
  UploadOutlined,
  PauseOutlined,
  CaretRightOutlined,
  SoundOutlined,
  FormOutlined,
  UnorderedListOutlined,
  WarningFilled,
} from "@ant-design/icons";
import {
  useMediaRecorder,
  mimeToExtension,
} from "../../hooks";
import {
  useCompass,
  useCreateMeeting,
  useUploadMeetingAudio,
  useTriggerMeetingTranscription,
  useImportMeetingAudio,
} from "../../api/queries";
import RecordingSaveForm, {
  type RecordingSavePayload,
} from "./RecordingSaveForm";
import RecordingList from "./RecordingList";
import styles from "../../styles/recording.module.css";

type PanelTab = "new" | "list";

interface RecordingPanelProps {
  open: boolean;
  onClose: () => void;
}

const SUPPORTED_FORMATS =
  "支持 wav / mp3 / m4a / aac / ogg / flac / mp4 / webm 格式，单个文件不超过 500 MB";

/** Visual progress track fills over this duration, then wraps. */
const PROGRESS_FULL_SECONDS = 60 * 60;

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function suggestTitle(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi} 录音`;
}

function stripExtension(filename: string): string {
  const idx = filename.lastIndexOf(".");
  return idx > 0 ? filename.slice(0, idx) : filename;
}

const TABS: { key: PanelTab; label: string; icon: React.ReactNode }[] = [
  { key: "new", label: "新建录音", icon: <FormOutlined /> },
  { key: "list", label: "录音列表", icon: <UnorderedListOutlined /> },
];

export default function RecordingPanel({ open, onClose }: RecordingPanelProps) {
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState<PanelTab>("new");
  const recorder = useMediaRecorder();
  const { data: compass } = useCompass();
  const createMeeting = useCreateMeeting();
  const uploadAudio = useUploadMeetingAudio();
  const triggerTranscription = useTriggerMeetingTranscription();
  const importAudio = useImportMeetingAudio();

  // After stop, show the save form. Cleared on successful save / reset.
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedMime, setCapturedMime] = useState<string | null>(null);
  const [capturedDuration, setCapturedDuration] = useState(0);

  // Import-audio flow state. When non-null, the panel renders the save form
  // for the picked file (instead of the live-recorded blob).
  const [importFile, setImportFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // When the recorder produces a fresh blob, snap it into local state so the
  // save form can use it independently of further recorder mutations.
  useEffect(() => {
    if (recorder.state === "idle" && recorder.blob && !capturedBlob) {
      setCapturedBlob(recorder.blob);
      setCapturedMime(recorder.mime);
      setCapturedDuration(recorder.durationSeconds);
    }
  }, [recorder.state, recorder.blob, recorder.mime, recorder.durationSeconds, capturedBlob]);

  // Reset everything when the Drawer closes — no stale state on next open.
  useEffect(() => {
    if (!open) {
      recorder.reset();
      setCapturedBlob(null);
      setCapturedMime(null);
      setCapturedDuration(0);
      setImportFile(null);
      setActiveTab("new");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleToggleRecording = useCallback(async () => {
    if (recorder.state === "idle") {
      // Clear any previously captured blob before starting a new take.
      setCapturedBlob(null);
      setCapturedMime(null);
      setCapturedDuration(0);
      await recorder.start();
    } else if (recorder.state === "recording") {
      recorder.pause();
    } else if (recorder.state === "paused") {
      recorder.resume();
    }
  }, [recorder]);

  const handleStop = useCallback(async () => {
    await recorder.stop();
  }, [recorder]);

  const handleReset = useCallback(() => {
    recorder.reset();
    setCapturedBlob(null);
    setCapturedMime(null);
    setCapturedDuration(0);
  }, [recorder]);

  const handleSave = useCallback(
    async (payload: RecordingSavePayload) => {
      if (!capturedBlob) return;
      try {
        const meeting = await createMeeting.mutateAsync({
          source: "manual",
          title: payload.title,
          matterId: payload.matterId ?? undefined,
          meetingType: payload.meetingType ?? undefined,
          isKeyMeeting: payload.isKeyMeeting,
        });
        const created = meeting?.data;
        if (!created?.id) {
          throw new Error("创建会议记录失败");
        }
        // Synthesize a filename from the title + the codec-derived extension.
        const ext = mimeToExtension(capturedMime);
        // Strip characters unsafe for filenames.
        const safeTitle = payload.title.replace(/[\\/:*?"<>|]/g, "_").slice(0, 100);
        const filename = `${safeTitle}${ext}`;
        const recordedAtIso = new Date().toISOString();

        await uploadAudio.mutateAsync({
          id: created.id,
          file: new File([capturedBlob], filename, { type: capturedMime ?? undefined }),
          options: {
            durationSeconds: capturedDuration,
            recordedAt: recordedAtIso,
          },
        });

        // Phase 2: auto-trigger ASR — recording + transcript both enter the
        // meeting record. Failures here are non-fatal (the audio is saved);
        // surface as a warning so the user can re-trigger from the list.
        try {
          await triggerTranscription.mutateAsync({ id: created.id });
          message.success("录音已保存，正在转写…");
        } catch (err) {
          console.warn("ASR trigger failed after save:", err);
          message.warning("录音已保存，但自动转写失败 — 可在列表里手动重新转写");
        }
        handleReset();
        setActiveTab("list");
      } catch (err) {
        message.error(`保存失败：${(err as Error)?.message ?? "请稍后重试"}`);
      }
    },
    [
      capturedBlob, capturedMime, capturedDuration,
      createMeeting, uploadAudio, triggerTranscription, handleReset,
    ],
  );

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImportFilePicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";  // reset so picking the same file again fires onChange
      if (!file) return;
      setImportFile(file);
    },
    [],
  );

  const handleImportCancel = useCallback(() => {
    setImportFile(null);
  }, [setImportFile]);

  const handleImportSave = useCallback(
    async (payload: RecordingSavePayload) => {
      if (!importFile) return;
      try {
        const meeting = await createMeeting.mutateAsync({
          source: "manual",
          title: payload.title,
          matterId: payload.matterId ?? undefined,
          meetingType: payload.meetingType ?? undefined,
          isKeyMeeting: payload.isKeyMeeting,
        });
        const created = meeting?.data;
        if (!created?.id) {
          throw new Error("创建会议记录失败");
        }

        // importAudio does upload + auto-trigger ASR in one call.
        await importAudio.mutateAsync({
          id: created.id,
          file: importFile,
          options: { autoTranscribe: true },
        });

        message.success("音频已导入，正在转写…");
        setImportFile(null);
        setActiveTab("list");
      } catch (err) {
        message.error(`导入失败：${(err as Error)?.message ?? "请稍后重试"}`);
      }
    },
    [importFile, createMeeting, importAudio],
  );

  const isRecording = recorder.state === "recording";
  const isPaused = recorder.state === "paused";
  const elapsed = isRecording || isPaused ? recorder.durationSeconds : capturedDuration;
  const progressPct = Math.min(100, (elapsed / PROGRESS_FULL_SECONDS) * 100);
  const submitting =
    createMeeting.isPending ||
    uploadAudio.isPending ||
    importAudio.isPending ||
    triggerTranscription.isPending;
  const matters = useMemo(() => compass?.recentMatters ?? [], [compass]);

  const renderNewTab = () => {
    if (importFile) {
      // Import-audio save form (parallels the live-recorded save form, but
      // submits via the import endpoint which auto-triggers ASR).
      return (
        <div className={styles.saveFormWrap}>
          <p className={styles.saveFormLead}>
            已选择文件「{importFile.name}」，填写信息后导入并自动转写。
          </p>
          <RecordingSaveForm
            matters={matters}
            defaultTitle={stripExtension(importFile.name)}
            submitting={submitting}
            submitLabel="导入并转写"
            onCancel={handleImportCancel}
            onSubmit={handleImportSave}
          />
        </div>
      );
    }

    if (capturedBlob) {
      // Save-form stage.
      return (
        <div className={styles.saveFormWrap}>
          <p className={styles.saveFormLead}>
            录音完成（{formatDuration(capturedDuration)}），填写信息后保存到录音列表。
          </p>
          <RecordingSaveForm
            matters={matters}
            defaultTitle={suggestTitle()}
            submitting={submitting}
            onCancel={handleReset}
            onSubmit={handleSave}
          />
        </div>
      );
    }

    // Recording stage.
    return (
      <>
        <div className={styles.timerBox}>
          <div className={styles.timerDigits}>
            <SoundOutlined className={styles.timerIcon} />
            <span className={styles.timerValue}>{formatDuration(elapsed)}</span>
          </div>
          <div className={styles.progressTrack} aria-hidden>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className={styles.coreArea}>
          {isRecording ? (
            <div className={styles.waveformActive} aria-label="正在录音">
              <span /><span /><span /><span /><span />
            </div>
          ) : (
            <div className={styles.waveformIdle}>
              <AudioOutlined />
            </div>
          )}
          <h3 className={styles.coreTitle}>
            {isRecording
              ? "正在录音"
              : isPaused
                ? "已暂停"
                : "开始新的录音"}
          </h3>
          <p className={styles.coreDesc}>
            {isRecording
              ? "点击暂停可临时中断，再次点击继续录制。"
              : isPaused
                ? "已暂停，点击按钮继续。"
                : "录音会在结束后保存到录音列表，可关联事项、撰写文字稿、标记为关键会议。"}
          </p>
          {recorder.error && (
            <p className={styles.coreError}>
              <WarningFilled /> {recorder.error}
            </p>
          )}
        </div>

        <div className={styles.actions}>
          <div className={styles.recordingRow}>
            <button
              type="button"
              className={`${styles.primaryBtn} ${
                isRecording || isPaused ? styles.primaryBtnRecording : ""
              }`}
              onClick={handleToggleRecording}
              disabled={recorder.state === "error"}
            >
              {isRecording ? <PauseOutlined /> : isPaused ? <CaretRightOutlined /> : <AudioOutlined />}
              {isRecording ? "暂停录音" : isPaused ? "继续录音" : "开始录音"}
            </button>
            {(isRecording || isPaused) && (
              <button
                type="button"
                className={styles.stopBtn}
                onClick={handleStop}
                aria-label="停止录音"
                title="停止"
              >
                ■
              </button>
            )}
          </div>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={handleImportClick}
            disabled={isRecording || isPaused}
          >
            <UploadOutlined />
            导入音频文件
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className={styles.hiddenInput}
            accept="audio/*,.wav,.mp3,.m4a,.aac,.ogg,.flac,.webm,.mp4"
            onChange={handleImportFilePicked}
          />
          <p className={styles.formats}>{SUPPORTED_FORMATS}</p>
          {!recorder.isSupported && (
            <p className={styles.coreError}>
              <WarningFilled /> 当前浏览器不支持录音功能
            </p>
          )}
        </div>
      </>
    );
  };

  return (
    <Drawer
      placement="right"
      open={open}
      onClose={onClose}
      size={420}
      closable={false}
      title={null}
      destroyOnHidden
      rootClassName={styles.drawerRoot}
      styles={{ body: { padding: 0 }, header: { display: "none" } }}
    >
      <div className={styles.panel}>
        <header className={styles.topBar}>
          <span className={styles.topBarTitle}>录音笔</span>
          <div className={styles.topBarActions}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={handleReset}
              aria-label="重置录音"
              title="重置"
              disabled={isRecording || isPaused || submitting}
            >
              <ReloadOutlined />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={onClose}
              aria-label="关闭录音笔"
            >
              <CloseOutlined />
            </button>
          </div>
        </header>

        <div className={styles.tabs} role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`${styles.tab} ${
                activeTab === tab.key ? styles.tabActive : ""
              }`}
              onClick={() => setActiveTab(tab.key)}
              disabled={isRecording || isPaused}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className={styles.body}>
          {activeTab === "new" ? renderNewTab() : <RecordingList />}
        </div>
      </div>
    </Drawer>
  );
}
